<template>
  <section class="jobs">
    <div class="container jobs__inner">
      <div
        v-if="backProfession || frontProfession"
        class="jobs__visual">
        <ShowcaseProfessionCard
          v-if="backProfession"
          :profession="backProfession"
          variant="back" />
        <ShowcaseProfessionCard
          v-if="frontProfession"
          :profession="frontProfession"
          variant="front" />
      </div>

      <div class="jobs__content">
        <h2 class="jobs__title">{{ t('jobs.title') }}</h2>
        <p class="jobs__text">{{ t('jobs.desc1') }}</p>
        <p class="jobs__text">{{ t('jobs.desc2') }}</p>
        <NuxtLink
          to="/metiers"
          class="btn jobs__cta">
          {{ t('jobs.cta') }}
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { ComputedRef, PropType } from 'vue'
import type { ShowcaseProfessionCardData } from '~/types/homeShowcases'
import ShowcaseProfessionCard from '~/components/cards/ShowcaseProfessionCard.vue'

const props = defineProps({
  professions: {
    type: Array as PropType<readonly ShowcaseProfessionCardData[]>,
    default: () => [],
  },
})

const { t } = useI18n()

const backProfession: ComputedRef<ShowcaseProfessionCardData | undefined> = computed(
  (): ShowcaseProfessionCardData | undefined => props.professions[0],
)

const frontProfession: ComputedRef<ShowcaseProfessionCardData | undefined> = computed(
  (): ShowcaseProfessionCardData | undefined => props.professions[1],
)
</script>

<style scoped>
.jobs {
  background: var(--bg-base);
  padding-block: var(--space-20);
  overflow: hidden;
}

.jobs__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-16);
  align-items: center;
}

.jobs__visual {
  position: relative;
  height: 480px;
}

.jobs__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.jobs__title {
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 800;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.jobs__text {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

.jobs__cta {
  width: fit-content;
  background: var(--gradient-violet);
  color: #fff;
  font-weight: 700;
  font-size: var(--text-base);
  padding: 0.75em 2em;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: filter var(--duration-fast) var(--ease-out);
  box-shadow: var(--shadow-violet);
  margin-top: var(--space-2);
}

.jobs__cta:hover {
  filter: brightness(1.1);
}

@media (max-width: 900px) {
  .jobs__inner {
    grid-template-columns: 1fr;
    gap: var(--space-10);
  }

  .jobs__content {
    order: -1;
  }

  .jobs__visual {
    height: 360px;
    max-width: 420px;
    margin-inline: auto;
    width: 100%;
  }

  .jobs__title {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }

  .jobs__cta {
    width: 100%;
    text-align: center;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .jobs__visual {
    height: 320px;
  }
}
</style>
